import React, { Component, ErrorInfo, ReactNode } from 'react'
import { Box, Button, Stack, SvgIcon, Typography } from '@mui/material'
import { ReactComponent as AtlasIcon } from '../../../assets/images/atlas-logo.svg'
import { connect, ConnectedProps } from 'react-redux'
import { RootState } from '../../../redux/types'

interface Props {
    children: ReactNode
}

interface State {
    hasError: boolean
    error: Error | null
    errorInfo: ErrorInfo | null
}

const mapStateToProps = (state: RootState) => ({
    isAdmin: state?.userRecord?.isAdmin ?? false,
})

const connector = connect(mapStateToProps)

type PropsFromRedux = ConnectedProps<typeof connector>

type ErrorBoundaryProps = Props & PropsFromRedux

class ErrorBoundary extends Component<ErrorBoundaryProps, State> {
    public state: State = {
        hasError: false,
        error: null,
        errorInfo: null,
    }

    public static getDerivedStateFromError(error: Error): Partial<State> {
        return { hasError: true, error }
    }

    public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        this.setState({ errorInfo: errorInfo })
        console.group('%cReact Error Boundary', 'color: red; font-weight: bold;')
        console.error('Error:', error)
        console.error('Component Stack:', errorInfo.componentStack)
        console.groupEnd()
    }

    private handleRefresh = () => {
        window.location.reload()
    }

    public render() {
        if (this.state.hasError) {
            return (
                <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    minHeight="100vh"
                    bgcolor="figma.grayscale.frameBackground"
                    p={3}
                >
                    <Stack
                        direction="column"
                        spacing={3}
                        alignItems="center"
                        textAlign="center"
                        p={4}
                        sx={{
                            backgroundColor: 'figma.grayscale.contentWrapper',
                            border: '1px solid',
                            borderColor: 'figma.grayscale.stroke',
                            borderRadius: 2,
                            maxWidth: '800px',
                            width: '100%',
                        }}
                    >
                        <SvgIcon
                            component={AtlasIcon}
                            inheritViewBox
                            sx={{ fontSize: '64px', color: 'figma.grayscale.textHier1' }}
                        />
                        <Typography variant="20x500" color="figma.grayscale.textHier1">
                            Oops! Something went wrong.
                        </Typography>
                        <Typography variant="18x400" color="figma.grayscale.textHier2">
                            We encountered an unexpected error. Please try refreshing the page.
                        </Typography>

                        {this.props.isAdmin && (this.state.error?.stack || this.state.errorInfo?.componentStack) && (
                            <Box
                                sx={{
                                    backgroundColor: 'figma.grayscale.table2',
                                    p: 2,
                                    borderRadius: 1,
                                    width: '100%',
                                    maxHeight: '300px',
                                    overflowY: 'auto',
                                    textAlign: 'left',
                                }}
                            >
                                <Typography
                                    component="pre"
                                    variant="14x400"
                                    color="figma.grayscale.textHier2"
                                    sx={{
                                        fontFamily: 'monospace',
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-all',
                                    }}
                                >
                                    {this.state.error?.message}
                                    {'\n\n'}
                                    {this.state.error?.stack}
                                    {this.state.errorInfo?.componentStack}
                                </Typography>
                            </Box>
                        )}

                        <Stack direction="row" spacing={2} mt={2}>
                            <Button
                                variant="contained"
                                onClick={this.handleRefresh}
                                sx={{
                                    backgroundColor: 'figma.primary',
                                    '&:hover': {
                                        backgroundColor: 'figma.primaryHover',
                                    },
                                }}
                            >
                                Refresh Page
                            </Button>
                            <Button
                                onClick={() => {
                                    window.location.href = '/reset'
                                }}
                                variant="outlined"
                                sx={{ color: 'figma.primary' }}
                            >
                                Reset ATLAS
                            </Button>
                        </Stack>
                    </Stack>
                </Box>
            )
        }

        return this.props.children
    }
}

export default connector(ErrorBoundary)
